import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from '@tanstack/react-router';
import * as stylex from '@stylexjs/stylex';
import '@astryxdesign/core/theme/tokens.stylex';
import { colorVars, fontWeightVars, radiusVars, spacingVars } from '@astryxdesign/core/theme/tokens.stylex';
import { Icon } from '@astryxdesign/core/Icon';
import { Badge } from '@astryxdesign/core/Badge';
import { Search } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { CyberAvatar, liquidGlass } from '@qronstat/ui';
import { sectionPermissions } from '@qronstat/shared';
import { useApp } from './context';
import { moreSheetOrder, navSections } from './navigation';
import type { MoreSheetKey } from './navigation';
import { openSearchEvent } from './events';
import { useUnreadAlerts } from '../api/references';
const motion = {
  default: '200ms',
  '@media (prefers-reduced-motion: reduce)': '0ms'
} as const;
const styles = {
  sheet: {
    kVAEAm: "xixxii4",
    kt4wiu: "x72tfeb",
    krVfgx: "x12w0cw2",
    kY2c9j: "x1orn75v",
    kzqmXN: "xfrui57",
    kskxy: "xpsdv8q",
    kORKVm: "x1odjw0f",
    kZeWKH: "xish69e",
    kB7OPa: "x9f619",
    kmVPX3: "xlsj2fj",
    k3nNDw: "x6ow6l",
    k3aq6I: "x1ib7wit",
    kSiTet: "xg01cxk",
    k1ekBW: "xwcsmn1",
    kIyJzY: "x13dflua x12w9bfk",
    kAMwcw: "xpkt1hu",
    $$css: true
  },
  sheetShown: {
    k3aq6I: "x1c071of",
    kSiTet: "x1hc1fzr",
    $$css: true
  },
  link: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xjcht0a",
    kzqmXN: "xh8yej3",
    kAzted: "x1wiwyrm",
    kB7OPa: "x9f619",
    kg3NbH: "xf314gf",
    kMwMTN: "x1tgivj0",
    kMv6JI: "xjb2p0i",
    kGuDYH: "x1qlqyl8",
    k63SB2: "x1e4wzip",
    k9WMMc: "x1yc453h",
    kybGjl: "x1hl2dhg",
    kkrTdU: "x1ypdohk",
    k3XXqK: "x1t137rt x9v5kkp",
    kMeerF: "x1de99jn",
    kjBf7l: "xj8wus6",
    kInvED: "x2ssjo2",
    kQizK7: "x1i10hfl",
    $$css: true
  }
};
interface SheetItem {
  key: MoreSheetKey;
  title: string;
  icon: LucideIcon;
  path: string | null;
}
export function MoreSheet({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    can,
    user
  } = useApp();
  const unread = useUnreadAlerts(can(sectionPermissions.alerts));
  const count = unread.data?.count ?? 0;
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const firstRef = useRef<HTMLAnchorElement | HTMLButtonElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setMounted(true);
      const frame = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = setTimeout(() => setMounted(false), 220);
    returnTo.current?.focus({
      preventScroll: true
    });
    return () => clearTimeout(timer);
  }, [open]);
  useEffect(() => {
    if (shown) firstRef.current?.focus({
      preventScroll: true
    });
  }, [shown]);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!mounted) return null;
  const items: SheetItem[] = moreSheetOrder.flatMap((key): SheetItem[] => {
    if (key === 'search') return [{
      key,
      title: intl.formatMessage({
        id: 'search.open'
      }),
      icon: Search,
      path: null
    }];
    const section = navSections.find(s => s.key === key);
    if (!section || section.permission && !can(section.permission)) return [];
    return [{
      key,
      title: intl.formatMessage({
        id: section.titleKey
      }),
      icon: section.icon,
      path: section.path
    }];
  });
  const media = (item: SheetItem): ReactNode => item.key === 'profile' ? <CyberAvatar index={user.avatar} size={36} /> : <span className="x3nfvp2 x6s0dn4 xl56j7k x2lah0s x14qfxbe xc9qbxq xh6dtrn xgcxg3y xjse4m1">
        <Icon icon={item.icon} />
      </span>;
  return <>
      <div aria-hidden="true" onClick={onClose} {...{
      0: {
        className: "xixxii4 x10a8y8t x1rqavoe xg5fgvj x19z9ohp x1m4ynbs xg01cxk x19991ni x13dflua x12w9bfk x1i10hfl"
      },
      1: {
        className: "xixxii4 x10a8y8t x1rqavoe xg5fgvj x19z9ohp x1m4ynbs x19991ni x13dflua x12w9bfk x1i10hfl x1hc1fzr"
      }
    }[!!shown << 0]} />
      <div role="dialog" aria-modal="true" aria-label={intl.formatMessage({
      id: 'nav.more'
    })} data-qs-more-sheet="" {...stylex.props(liquidGlass.pane, styles.sheet, shown && styles.sheetShown)}>
        <ul className="x78zum5 x3ieub6 x1s4dlld x1ghz6dp x1717udv x3ct3a4">
          {items.map((item, index) => {
          const body = <>
                {media(item)}
                <span className="x1iyjqo2 xeuugli">{item.title}</span>
                {item.key === 'alerts' && count > 0 ? <Badge label={count > 99 ? '99+' : String(count)} variant="warning" /> : null}
              </>;
          return <li key={item.key} data-qs-more-item={item.key} {...{
            0: {
              className: "xg01cxk x1rbuhwb xwcsmn1 x13dflua x12w9bfk"
            },
            1: {
              className: "xwcsmn1 x13dflua x12w9bfk x1hc1fzr x1c071of"
            }
          }[!!shown << 0]} style={{
            transitionDelay: shown ? `${index * 18}ms` : '0ms'
          }}>
                {item.path ? <a ref={index === 0 ? el => {
              firstRef.current = el;
            } : undefined} href={item.path} {...stylex.props(liquidGlass.tile, styles.link)} onClick={event => {
              event.preventDefault();
              onClose();
              void navigate({
                to: item.path ?? '/'
              });
            }}>
                    {body}
                  </a> : <button ref={index === 0 ? el => {
              firstRef.current = el;
            } : undefined} type="button" {...stylex.props(liquidGlass.tile, styles.link)} onClick={() => {
              onClose();
              window.dispatchEvent(new Event(openSearchEvent));
            }}>
                    {body}
                  </button>}
              </li>;
        })}
        </ul>
      </div>
    </>;
}